import { useMemo, useState } from "react";
import { Alert } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import { useGetAllCategories } from "../../../../../api/jeep-rally/categories/categories.hooks";
import { useCreateRallyMutation } from "../../../../../api/jeep-rally/rally/rally.hooks";
import { useGetAllRoutes } from "../../../../../api/jeep-rally/routes/routes.hooks";
import { useGetAllTypes } from "../../../../../api/jeep-rally/types/types.hooks";
import { normalizeCategories } from "../../Categories/utils/category.utils";
import { normalizeRoutes } from "../../Routes/utils/route.utils";
import { normalizeTypes } from "../../Types/utils/type.utils";
import RallyForm from "../_components/RallyForm";
import {
  buildRallyPayload,
  getRallyApiErrorMessage,
  initialRallyForm,
  validateRallyForm,
} from "../utils/rally.utils";

const RallyCreatePage = () => {
  const history = useHistory();
  const [form, setForm] = useState(initialRallyForm);
  const [validationErrors, setValidationErrors] = useState({});
  const [submitError, setSubmitError] = useState("");

  const {
    mutateAsync: createRally,
    isLoading,
    isPending,
    reset,
  } = useCreateRallyMutation();

  const submitting = isLoading || isPending;

  const {
    data: routesData,
    isLoading: isRoutesLoading,
    isError: isRoutesError,
  } = useGetAllRoutes({ status: "active" });
  const routes = useMemo(() => normalizeRoutes(routesData), [routesData]);

  const {
    data: typesData,
    isLoading: isTypesLoading,
    isError: isTypesError,
  } = useGetAllTypes({ status: "active" });
  const types = useMemo(() => normalizeTypes(typesData), [typesData]);

  const {
    data: categoriesData,
    isLoading: isCategoriesLoading,
    isError: isCategoriesError,
  } = useGetAllCategories({ status: "active" });
  const categories = useMemo(
    () => normalizeCategories(categoriesData),
    [categoriesData]
  );

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setValidationErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    reset();
    setSubmitError("");

    const nextErrors = validateRallyForm(form);
    setValidationErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    try {
      await createRally(buildRallyPayload(form, { isEdit: false }));
      history.push("/jeep-rally/rallies");
    } catch (error) {
      setSubmitError(
        getRallyApiErrorMessage(
          error,
          "Failed to create rally. Please try again."
        )
      );
    }
  };

  const goBack = () => history.push("/jeep-rally/rallies");

  return (
    <div className="container-fluid">
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-link p-0 text-decoration-none"
              onClick={goBack}
              aria-label="Back to rallies list"
              title="Back"
            >
              <i className="bi bi-arrow-left fs-4" />
            </button>
            <h4 className="card-title mb-0">Create Rally</h4>
          </div>
        </div>

        <div className="card-body">
          <p className="text-muted mb-4">
            Select a route and types first, then choose categories with pricing.
            Images are uploaded to gallery first; videos use URL links.
          </p>

          {submitError && (
            <Alert variant="danger" className="mb-4">
              {submitError}
            </Alert>
          )}

          <RallyForm
            form={form}
            validationErrors={validationErrors}
            submitting={submitting}
            submitLabel="Create Rally"
            submittingLabel="Creating..."
            isEdit={false}
            onSubmit={handleSubmit}
            onFieldChange={updateField}
            onBack={goBack}
            routes={routes}
            isRoutesLoading={isRoutesLoading}
            isRoutesError={isRoutesError}
            types={types}
            isTypesLoading={isTypesLoading}
            isTypesError={isTypesError}
            categories={categories}
            isCategoriesLoading={isCategoriesLoading}
            isCategoriesError={isCategoriesError}
          />
        </div>
      </div>
    </div>
  );
};

export default RallyCreatePage;
